import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { AlertTriangle, X } from 'lucide-react';
import { useScrollLock } from '../hooks/useScrollLock';

const ModalOverlay = styled.div`
  position: fixed;
  top: 74px;
  left: 0;
  width: 420px;
  height: calc(100vh - 74px);
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  padding: 1rem;
  pointer-events: auto;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 340px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const IconContainer = styled.div`
  background: #fef2f2;
  border-radius: 50%;
  padding: 0.75rem;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const Message = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'cancel' && `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
  
  ${props => props.variant === 'delete' && `
    background: #dc2626;
    color: white;
    
    &:hover {
      background: #b91c1c;
    }
    
    &:disabled {
      background: #fca5a5;
      cursor: not-allowed;
    }
  `}
`;

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  loanId, 
  isLoading = false 
}) => {
  // Lock body scroll when modal is open
  useScrollLock(isOpen);


  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay>
      <ModalContent>
        <Header>
          <IconContainer>
            <AlertTriangle size={24} color="#dc2626" />
          </IconContainer>
          <Title>Permanently Delete Results</Title>
        </Header>
        
        <Message>
          Are you sure you want to permanently delete all extraction results for loan <strong>{loanId}</strong>? 
          This action cannot be undone and will remove all field data from the database.
        </Message>
        
        <ButtonGroup>
          <Button variant="cancel" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="delete" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );

  // Render modal outside of the component hierarchy using portal
  return createPortal(modalContent, document.body);
};

export default DeleteConfirmationModal;
